// @ts-nocheck
/**
 * ドラフトシステム - デッキ構築フック
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { DRAFT_CONFIG } from '../constants';
import { validateDeck, calculateDeckStats, generateCostCurve } from '../systems/deckValidator';

/**
 * デッキ構築フック
 * @param {Object} session - ドラフトセッション
 * @param {Object} callbacks - コールバック群
 * @param {Function} callbacks.onAddToDeck - デッキ追加
 * @param {Function} callbacks.onRemoveFromDeck - デッキから削除
 * @param {Function} callbacks.onConfirm - デッキ確定
 * @returns {Object} デッキ構築操作
 */
export const useDraftDeckBuilder = (session, callbacks = {}) => {
  const { onAddToDeck, onRemoveFromDeck, onConfirm } = callbacks;

  // ========================================
  // 状態
  // ========================================
  const [selectedCard, setSelectedCard] = useState(null);
  const [sortMode, setSortMode] = useState('cost'); // 'cost', 'type', 'attribute', 'rarity'
  const [filterType, setFilterType] = useState('all'); // 'all', 'monster', 'magic', 'field', 'phase'
  const [timeRemaining, setTimeRemaining] = useState(DRAFT_CONFIG.DECK_BUILD_TIME_LIMIT);
  const timerRef = useRef(null);

  // ========================================
  // デッキ/サイドボード
  // ========================================

  const deckBuilding = session?.deckBuilding?.player || { deck: [], sideboard: [] };
  const { deck, sideboard, isConfirmed } = deckBuilding;

  // ========================================
  // タイマー
  // ========================================

  useEffect(() => {
    // デッキ構築フェーズでなければタイマー停止
    if (!session || session.status !== 'building' || isConfirmed) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // タイマー開始
    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          // タイムアウト - 自動確定
          if (onConfirm) {
            setTimeout(() => onConfirm(), 0);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [session, isConfirmed, onConfirm]);

  // ========================================
  // バリデーション
  // ========================================

  const validation = useMemo(() => {
    return validateDeck(deck);
  }, [deck]);

  const stats = useMemo(() => {
    return calculateDeckStats(deck);
  }, [deck]);

  const costCurve = useMemo(() => {
    return generateCostCurve(deck);
  }, [deck]);

  // ========================================
  // ソート/フィルター
  // ========================================

  /**
   * カードをソート
   * @param {Object[]} cards - カード配列
   * @returns {Object[]} ソート済み配列
   */
  const sortCards = useCallback((cards) => {
    return [...cards].sort((a, b) => {
      switch (sortMode) {
        case 'cost':
          return (a.cost || 0) - (b.cost || 0);
        case 'type':
          return (a.type || '').localeCompare(b.type || '');
        case 'attribute':
          return (a.attribute || '').localeCompare(b.attribute || '');
        case 'rarity': {
          const rarityOrder = ['C', 'UC', 'R', 'SR', 'UR', 'HR', 'SEC', 'ALT', 'SP', 'GR'];
          return rarityOrder.indexOf(b.rarity) - rarityOrder.indexOf(a.rarity);
        }
        default:
          return 0;
      }
    });
  }, [sortMode]);

  /**
   * カードをフィルター
   * @param {Object[]} cards - カード配列
   * @returns {Object[]} フィルター済み配列
   */
  const filterCards = useCallback((cards) => {
    if (filterType === 'all') return cards;
    return cards.filter(card => card.type === filterType);
  }, [filterType]);

  /**
   * ソート&フィルター済みのデッキ
   */
  const sortedDeck = useMemo(() => {
    return sortCards(filterCards(deck));
  }, [deck, sortCards, filterCards]);

  /**
   * ソート&フィルター済みのサイドボード
   */
  const sortedSideboard = useMemo(() => {
    return sortCards(filterCards(sideboard));
  }, [sideboard, sortCards, filterCards]);

  // ========================================
  // 操作
  // ========================================

  /**
   * カードを選択
   */
  const selectCard = useCallback((card) => {
    setSelectedCard(card);
  }, []);

  /**
   * 選択をクリア
   */
  const clearSelection = useCallback(() => {
    setSelectedCard(null);
  }, []);

  /**
   * サイドボードからデッキに追加
   * @param {string} cardId - カードID
   */
  const addToDeck = useCallback((cardId) => {
    if (isConfirmed) return false;
    if (deck.length >= DRAFT_CONFIG.MAX_DECK_SIZE) return false;

    if (onAddToDeck) {
      const success = onAddToDeck(cardId);
      if (success) setSelectedCard(null);
      return success;
    }
    return false;
  }, [deck.length, isConfirmed, onAddToDeck]);

  /**
   * デッキからサイドボードに戻す
   * @param {string} cardId - カードID
   */
  const removeFromDeck = useCallback((cardId) => {
    if (isConfirmed) return false;

    if (onRemoveFromDeck) {
      const success = onRemoveFromDeck(cardId);
      if (success) setSelectedCard(null);
      return success;
    }
    return false;
  }, [isConfirmed, onRemoveFromDeck]);

  /**
   * デッキを確定
   */
  const confirmDeck = useCallback(() => {
    if (isConfirmed) return false;
    if (!validation.isValid) return false;

    if (onConfirm) {
      return onConfirm();
    }
    return false;
  }, [isConfirmed, validation.isValid, onConfirm]);

  /**
   * 自動構築（推奨）
   */
  const autoBuild = useCallback(() => {
    if (isConfirmed) return;

    // 全カードをプール
    const allCards = [...deck, ...sideboard];

    // スコアリング（シンプル版）
    const scored = allCards.map(card => {
      let score = 0;

      // レアリティ
      const rarityScores = { C: 10, UC: 20, R: 40, SR: 70, UR: 100, HR: 150, SEC: 200 };
      score += rarityScores[card.rarity] || 10;

      // モンスター優先
      if (card.type === 'monster') score += 30;

      // コスト曲線
      const cost = card.cost || 0;
      if (cost <= 2) score += 20;
      else if (cost <= 4) score += 15;
      else score += 5;

      return { card, score };
    });

    // スコア順でソート
    scored.sort((a, b) => b.score - a.score);

    // 上位35枚をデッキに
    const newDeck = scored.slice(0, DRAFT_CONFIG.RECOMMENDED_DECK_SIZE).map(s => s.card);
    const newSideboard = scored.slice(DRAFT_CONFIG.RECOMMENDED_DECK_SIZE).map(s => s.card);

    // 一枚ずつ操作して更新
    // (実際の実装ではバッチ更新を検討)
    deck.forEach(card => {
      if (!newDeck.find(c => c.id === card.id)) {
        onRemoveFromDeck?.(card.id);
      }
    });

    newDeck.forEach(card => {
      if (!deck.find(c => c.id === card.id)) {
        onAddToDeck?.(card.id);
      }
    });
  }, [deck, sideboard, isConfirmed, onAddToDeck, onRemoveFromDeck]);

  // ========================================
  // 返却値
  // ========================================
  return {
    // 状態
    deck,
    sideboard,
    selectedCard,
    sortMode,
    filterType,
    timeRemaining,
    isConfirmed,

    // バリデーション
    validation,
    stats,
    costCurve,

    // ソート済み
    sortedDeck,
    sortedSideboard,

    // 操作
    selectCard,
    clearSelection,
    addToDeck,
    removeFromDeck,
    confirmDeck,
    autoBuild,

    // 設定
    setSortMode,
    setFilterType,

    // 便利なプロパティ
    deckCount: deck.length,
    sideboardCount: sideboard.length,
    totalCount: deck.length + sideboard.length,
    canConfirm: validation.isValid && !isConfirmed,
    canAddMore: deck.length < DRAFT_CONFIG.MAX_DECK_SIZE,
    isMinimumMet: deck.length >= DRAFT_CONFIG.MIN_DECK_SIZE,
  };
};

export default useDraftDeckBuilder;
